import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

export default function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select...",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const updatePosition = () => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuMaxHeight = 260;
    // Horizontal clamp — keep menu within viewport
    let left = r.left;
    const width = Math.min(r.width, vw - margin * 2);
    if (left + width > vw - margin) left = vw - margin - width;
    if (left < margin) left = margin;
    // Vertical: open below by default. If not enough space below, open above
    // and pin the menu's BOTTOM to just above the trigger so it visually
    // attaches to the field regardless of its actual height.
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    let top = r.bottom + 4;
    let bottom = null;
    if (spaceBelow < 160 && spaceAbove > spaceBelow) {
      // Use the menu's measured height if available, otherwise menuMaxHeight
      const measured = menuRef.current
        ? menuRef.current.getBoundingClientRect().height
        : menuMaxHeight;
      const menuHeight = Math.min(measured || menuMaxHeight, spaceAbove - 4);
      top = r.top - menuHeight - 4;
      bottom = vh - r.top + 4; // anchor by bottom to avoid gap on actual-height
    }
    setPos({ top, left, width, bottom });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let rafId;
    let lastTop = -1;
    let lastLeft = -1;
    let lastWidth = -1;
    // Continuously track the trigger's position so the menu always sits
    // below it, even when ancestors scroll (modals, internal containers, etc).
    const tick = () => {
      if (!wrapRef.current) return;
      const r = wrapRef.current.getBoundingClientRect();
      if (r.bottom !== lastTop || r.left !== lastLeft || r.width !== lastWidth) {
        lastTop = r.bottom;
        lastLeft = r.left;
        lastWidth = r.width;
        updatePosition();
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    const onClickAway = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("touchstart", onClickAway);
    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("touchstart", onClickAway);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const label = selected ? selected.label : placeholder;

  return (
    <div
      ref={wrapRef}
      className={"dd-wrap" + (open ? " dd-open" : "") + (className ? " " + className : "")}
    >
      <button
        type="button"
        className="dd-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={"dd-label" + (selected ? "" : " dd-placeholder")}>{label}</span>
        <i className="ti ti-chevron-down dd-caret"></i>
      </button>
      {open &&
        createPortal(
          <ul
            ref={menuRef}
            className="dd-menu dd-menu-portal"
            role="listbox"
            style={{
              position: "fixed",
              ...(pos.bottom != null
                ? { bottom: `${pos.bottom}px` }
                : { top: `${pos.top}px` }),
              left: `${pos.left}px`,
              width: `${pos.width}px`,
            }}
          >
            {options.map((o) => (
              <li
                key={o.value || "__empty__"}
                role="option"
                aria-selected={o.value === value}
                className={"dd-item" + (o.value === value ? " active" : "")}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}
